<template>
    <div class="rating-input" :class="{ 'rating-input--disabled': disabled }">
        <div
            ref="group"
            class="rating-input__group"
            role="radiogroup"
            :aria-label="label"
            :aria-invalid="invalid ? 'true' : 'false'"
            :aria-describedby="ariaDescribedBy"
            @mouseleave="clearPreview"
        >
            <span v-for="star in 5" :key="star" class="rating-input__star">
                <svg class="rating-input__star-base text-foxy-border" aria-hidden="true" viewBox="0 0 24 24">
                    <path :d="starPath" />
                </svg>
                <span class="rating-input__star-fill" :style="{ width: `${fillForStar(star)}%` }" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                        <path :d="starPath" />
                    </svg>
                </span>

                <button
                    v-for="half in 2"
                    :key="half"
                    ref="options"
                    type="button"
                    class="rating-input__option"
                    :class="[half === 1 ? 'rating-input__option--left' : 'rating-input__option--right']"
                    role="radio"
                    :aria-checked="committedValue === optionValue(star, half) ? 'true' : 'false'"
                    :aria-label="optionLabel(optionValue(star, half))"
                    :data-rating-option="optionValue(star, half)"
                    :disabled="disabled"
                    :tabindex="disabled ? -1 : focusValue === optionValue(star, half) ? 0 : -1"
                    @mouseenter="preview(optionValue(star, half))"
                    @focus="handleFocus(optionValue(star, half))"
                    @click="commit(optionValue(star, half))"
                    @keydown="handleKeydown($event, optionValue(star, half))"
                    @blur="handleBlur"
                >
                    <span class="sr-only">{{ optionLabel(optionValue(star, half)) }}</span>
                </button>
            </span>
        </div>

        <div v-if="!externalMeta || canClear" class="rating-input__meta" :aria-live="externalMeta ? null : 'polite'">
            <template v-if="!externalMeta">
                <span v-if="displayValue !== null" class="rating-input__value text-foxy-text">
                    {{ formatRatingLabel(displayValue) }}
                </span>
                <span v-else class="rating-input__empty text-foxy-text-secondary">{{ emptyLabel }}</span>
            </template>
            <button v-if="canClear" type="button" class="rating-input__clear" :class="{ 'rating-input__clear--quiet': externalMeta }" @click="clear">
                {{ clearLabel }}
            </button>
        </div>
    </div>
</template>

<script>
import { formatRatingLabel, formatStarRating, normalizeRating } from '../utils/rating';

export default {
    name: 'RatingInput',
    props: {
        value: {
            type: Number,
            default: null,
        },
        label: {
            type: String,
            default: 'Оцінка книги',
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        errorId: {
            type: String,
            default: '',
        },
        describedBy: {
            type: String,
            default: '',
        },
        clearLabel: {
            type: String,
            default: 'Очистити',
        },
        emptyLabel: {
            type: String,
            default: 'Оцінку не вибрано',
        },
        externalMeta: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const initial = normalizeRating(this.value);

        return {
            previewValue: null,
            focusValue: initial === null ? 1 : initial,
            starPath: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
        };
    },
    computed: {
        committedValue() {
            return normalizeRating(this.value);
        },
        displayValue() {
            return this.previewValue === null ? this.committedValue : this.previewValue;
        },
        canClear() {
            return this.clearable && this.committedValue !== null && !this.disabled;
        },
        ariaDescribedBy() {
            const ids = [this.describedBy, this.invalid ? this.errorId : ''].flatMap((value) => String(value || '').split(/\s+/)).filter(Boolean);

            return [...new Set(ids)].join(' ') || null;
        },
    },
    watch: {
        committedValue(value) {
            this.previewValue = null;
            this.focusValue = value === null ? 1 : value;
        },
    },
    methods: {
        formatRatingLabel,
        focus() {
            this.focusOption(this.committedValue === null ? this.focusValue : this.committedValue);
        },
        optionValue(star, half) {
            return (star - 1) * 2 + half;
        },
        optionLabel(value) {
            return `${formatStarRating(value)} з 5 зірок, ${value} з 10`;
        },
        fillForStar(star) {
            const value = this.displayValue === null ? 0 : this.displayValue;
            const before = (star - 1) * 2;

            if (value >= before + 2) return 100;
            if (value === before + 1) return 50;
            return 0;
        },
        preview(value) {
            if (!this.disabled) {
                this.previewValue = value;
                this.focusValue = value;
            }
        },
        handleFocus(value) {
            if (this.disabled) return;

            this.focusValue = value;
            this.previewValue = this.committedValue === null ? null : value;
        },
        clearPreview() {
            this.previewValue = null;
        },
        commit(value) {
            if (this.disabled) return;

            const rating = normalizeRating(value);
            if (rating === null) return;

            this.focusValue = rating;
            this.previewValue = null;
            this.$emit('input', rating);
            this.$emit('change', rating);
        },
        clear() {
            this.previewValue = null;
            this.focusValue = 1;
            this.$emit('input', null);
            this.$emit('change', null);
            this.$nextTick(() => this.focusOption(1));
        },
        handleKeydown(event, current) {
            let next = null;

            if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = Math.max(1, current - 1);
            if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = Math.min(10, current + 1);
            if (event.key === 'Home') next = 1;
            if (event.key === 'End') next = 10;

            if (next !== null) {
                event.preventDefault();
                this.commit(next);
                this.$nextTick(() => this.focusOption(next));
                return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.commit(current);
            }
        },
        focusOption(value) {
            const options = this.$refs.options || [];
            const option = options.find((item) => Number(item.dataset.ratingOption) === value);
            option?.focus();
        },
        handleBlur() {
            this.$nextTick(() => {
                if (!this.$refs.group?.contains(document.activeElement)) {
                    this.clearPreview();
                }
            });
        },
    },
};
</script>

<style scoped>
.rating-input {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
}

.rating-input__group {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.rating-input__star {
    position: relative;
    display: inline-block;
    width: 2.75rem;
    height: 2.75rem;
    flex: 0 0 2.75rem;
}

.rating-input__star-base,
.rating-input__star-fill svg {
    position: absolute;
    top: 0.375rem;
    left: 0.375rem;
    width: 2rem;
    height: 2rem;
}

.rating-input__star-base {
    fill: transparent;
    stroke: currentColor;
    stroke-width: 1.5;
}

.rating-input__star-fill {
    position: absolute;
    inset: 0 auto 0 0;
    overflow: hidden;
    color: rgb(var(--foxy-honey));
    pointer-events: none;
}

.rating-input__star-fill svg {
    fill: currentColor;
    stroke: currentColor;
    stroke-width: 1.5;
}

.rating-input__option {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 2;
    width: 50%;
    border: 0;
    border-radius: 0.375rem;
    background: transparent;
    cursor: pointer;
}

.rating-input__option--left {
    left: 0;
}

.rating-input__option--right {
    right: 0;
}

.rating-input__option:focus-visible {
    outline: 3px solid rgb(var(--foxy-honey));
    outline-offset: 2px;
}

.rating-input__meta {
    display: flex;
    min-height: 1.5rem;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.875rem;
}

.rating-input__value {
    font-weight: 600;
}

.rating-input__clear {
    min-height: 2.75rem;
    border: 0;
    background: transparent;
    color: rgb(var(--foxy-error));
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 0.2em;
}

.rating-input__clear--quiet {
    color: rgb(var(--foxy-text-secondary));
}

.rating-input--disabled {
    opacity: 0.6;
}
</style>
